import React, { useEffect, useState } from "react";
import { Modal, Button, Col, Row } from "react-bootstrap";
import {
  getWeeklyRewardDetails,
  updateWeeklyRewardHistory,
} from "store/actions";
import { useDispatch, useSelector } from "react-redux";
import { DATE_FORMAT } from "helpers/common";
import { FadeLoader } from "react-spinners";
import { toast } from "react-toastify";

export default function WeeklyRewardModal({ show, handleClose, title, id }) {
  const { loading } = useSelector(({ Jackpot }) => Jackpot);
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      fetchData();
      setIsEditing(false);
    } else {
      setData(null);
    }
  }, [id]);

  const fetchData = () => {
    dispatch(
      getWeeklyRewardDetails({
        data: { _id: id },
        callBack: (err, response) => {
          if (!err) {
            setData({
              _id: response?.data?._id,
              weekNo: response?.data?.weekNo,
              year: response?.data?.year,
              lastWeekEarning: response?.data?.lastWeekEarning,
              prevWeekLeftAmount: response?.data?.prevWeekLeftAmount,
              totalWeeklyBudget: response?.data?.totalWeeklyBudget,
              jackpotBudgetTotal: response?.data?.jackpotBudgetTotal,
              jackpotBudgetVisible: response?.data?.jackpotBudgetVisible,
              jackpotBudgetReserve: response?.data?.jackpotBudgetReserve,
              grandJackpotVisible: response?.data?.grandJackpotVisible,
              majorJackpotVisible: response?.data?.majorJackpotVisible,
              minorJackpotVisible: response?.data?.minorJackpotVisible,
              grandJackpotReserve: response?.data?.grandJackpotReserve,
              majorJackpotReserve: response?.data?.majorJackpotReserve,
              minorJackpotReserve: response?.data?.minorJackpotReserve,
              date_created_utc: response?.data?.date_created_utc,
              date_modified_utc: response?.data?.date_modified_utc,
            });
          }
        },
      }),
    );
  };

  const handleSave = () => {
    let updatedData = {
      _id: data?._id,
      weekNo: data?.weekNo,
      year: data?.year,
      totalWeeklyBudget: data?.totalWeeklyBudget,
      jackpotBudgetTotal: data?.jackpotBudgetTotal,
      jackpotBudgetVisible: data?.jackpotBudgetVisible,
      jackpotBudgetReserve: data?.jackpotBudgetReserve,
      grandJackpotVisible: data?.grandJackpotVisible,
      majorJackpotVisible: data?.majorJackpotVisible,
      minorJackpotVisible: data?.minorJackpotVisible,
      grandJackpotReserve: data?.grandJackpotReserve,
      majorJackpotReserve: data?.majorJackpotReserve,
      minorJackpotReserve: data?.minorJackpotReserve,
    };
    const hasRewardBudgetChanged =
      Number(data?.jackpotBudgetTotal) !==
      Number(updatedData.jackpotBudgetTotal);

    if (hasRewardBudgetChanged) {
      const jackpotTotal = Number(updatedData.jackpotBudgetTotal);

      if (jackpotTotal < Number(updatedData.totalWeeklyBudget)) {
        const confirmProceed = window.confirm(
          `Warning: The jackpot budget (${jackpotTotal}) does not match the total weekly budget (${updatedData.totalWeeklyBudget}).
                         Do you want to continue anyway?`,
        );

        if (!confirmProceed) return;
      } else if (rewardSplitTotal > updatedData.totalWeeklyBudget) {
        console.warn(
          "TThe jackpot budget must not exceed the total weekly budget.",
        );
        return;
      }
    }

    const jackpotSplitTotal =
      Number(updatedData.jackpotBudgetVisible) +
      Number(updatedData.jackpotBudgetReserve);
    if (jackpotSplitTotal < updatedData.jackpotBudgetTotal) {
      const jackpotSplit = window.confirm(
        `Warning: The sum of jackpot visible budget (${updatedData.jackpotBudgetVisible}),
                jackpot reserve budget (${updatedData.jackpotBudgetReserve}) is ${jackpotSplitTotal}. 
                It should be equal to total jackpot budget (${updatedData.jackpotBudgetTotal}).
                Do you want to continue anyway?`,
      );
      if (!jackpotSplit) return; // User chose not to continue
    } else if (jackpotSplitTotal > updatedData.jackpotBudgetTotal) {
      console.warn(
        "The total of jackpot visible and reserved budget must equal total jackpot budget!",
      );
      return;
    }

    const jackpotBudgetVisible =
      Number(updatedData.grandJackpotVisible) +
      Number(updatedData.majorJackpotVisible) +
      Number(updatedData.minorJackpotVisible);
    if (jackpotBudgetVisible < updatedData.jackpotBudgetVisible) {
      const visibleJackpotSplit = window.confirm(
        `Warning: The sum of grand jackpot visible budget (${updatedData.grandJackpotVisible}),
                major jackpot visible budget (${updatedData.majorJackpotVisible}) and minor jackpot visible 
                budget (${updatedData.minorJackpotVisible}) is ${jackpotBudgetVisible}. 
                It should be equal to total visible jackpot budget (${updatedData.jackpotBudgetVisible}).
                Do you want to continue anyway?`,
      );
      if (!visibleJackpotSplit) return; // User chose not to continue
    } else if (jackpotBudgetVisible > updatedData.jackpotBudgetVisible) {
      console.warn(
        "The total of grand, major and minor jackpot visible must equal total visible jackpot budget!",
      );
      return;
    }

    const jackpotBudgetReserved =
      Number(updatedData.grandJackpotReserve) +
      Number(updatedData.majorJackpotReserve) +
      Number(updatedData.minorJackpotReserve);
    if (jackpotBudgetReserved < updatedData.jackpotBudgetReserve) {
      const reservedJackpotSplit = window.confirm(
        `Warning: The sum of grand jackpot reserved budget (${updatedData.grandJackpotReserve}),
                major jackpot reserved budget (${updatedData.majorJackpotReserve}) and minor jackpot reserved 
                budget (${updatedData.minorJackpotReserve}) is ${jackpotBudgetReserved}. 
                It should be equal to total reserved jackpot budget (${updatedData.jackpotBudgetReserve}).
                Do you want to continue anyway?`,
      );
      if (!reservedJackpotSplit) return; // User chose not to continue
    } else if (jackpotBudgetReserved > updatedData.jackpotBudgetReserve) {
      console.warn(
        "The total of grand, major and minor jackpot reserverd must equal total reserved jackpot budget!",
      );
      return;
    }

    dispatch(
      updateWeeklyRewardHistory({
        data: updatedData,
        callBack: (err, response) => {
          if (!err) {
            setIsEditing(false);
            setData({
              _id: response?.data?._id,
              weekNo: response?.data?.weekNo,
              year: response?.data?.year,
              lastWeekEarning: response?.data?.lastWeekEarning,
              prevWeekLeftAmount: response?.data?.prevWeekLeftAmount,
              totalWeeklyBudget: response?.data?.totalWeeklyBudget,
              jackpotBudgetTotal: response?.data?.jackpotBudgetTotal,
              jackpotBudgetVisible: response?.data?.jackpotBudgetVisible,
              jackpotBudgetReserve: response?.data?.jackpotBudgetReserve,
              grandJackpotVisible: response?.data?.grandJackpotVisible,
              majorJackpotVisible: response?.data?.majorJackpotVisible,
              minorJackpotVisible: response?.data?.minorJackpotVisible,
              grandJackpotReserve: response?.data?.grandJackpotReserve,
              majorJackpotReserve: response?.data?.majorJackpotReserve,
              minorJackpotReserve: response?.data?.minorJackpotReserve,
              date_created_utc: response?.data?.date_created_utc,
              date_modified_utc: response?.data?.date_modified_utc,
            });
            setTimeout(() => {
              handleClose();
            }, 100);
          } else {
            toast.error("Failed to save changes");
          }
        },
      }),
    );
  };

  // Helper to render either text or input based on isEditing
  const renderField = (label, valueKey, type = "text") => {
    return (
      <li className="col-lg-4 col-sm-6 my-2" key={valueKey}>
        <p>
          {label} :{" "}
          <span className="font-weight-bold">
            {data?.[valueKey] !== undefined && data?.[valueKey] !== null
              ? data[valueKey]
              : "--"}
          </span>
        </p>
      </li>
    );
  };

  // For fields with dollar sign values, we can keep the $ prefix and edit just the number
  const renderCurrencyField = (label, valueKey) => {
    return (
      <li className="col-lg-4 col-sm-6 my-2" key={valueKey}>
        <p>
          {label} :{" "}
          {isEditing ? (
            <input
              type="number"
              min="0"
              className="form-control form-control-sm"
              value={data?.[valueKey] ?? ""}
              onChange={(e) =>
                setData({
                  ...data,
                  [valueKey]: parseFloat(e.target.value) || 0,
                })
              }
            />
          ) : (
            <span className="font-weight-bold">
              {`$ ${data?.[valueKey]?.toFixed(2) || "0.00"}`}
            </span>
          )}
        </p>
      </li>
    );
  };

  const renderCurrencyNonEditField = (label, valueKey) => {
    return (
      <li className="col-lg-4 col-sm-6 my-2" key={valueKey}>
        <p>
          {label} :{" "}
          <span className="font-weight-bold">
            {`$ ${data?.[valueKey]?.toFixed(2) || "0.00"}`}
          </span>
        </p>
      </li>
    );
  };

  const renderIconNonEditField = (label, valueKey, iconKey) => {
    return (
      <li className="col-lg-4 col-sm-6 my-2" key={valueKey}>
        <p>
          {label} :{" "}
          <span className="font-weight-bold">
            <img
              src={data?.[iconKey]}
              alt={label}
              style={{
                marginRight: "5px",
                verticalAlign: "middle",
                height: "25px",
                width: "25px",
              }}
            />
            {data?.[valueKey]?.toFixed(5) || "0.00"}
          </span>
        </p>
      </li>
    );
  };

  return loading ? (
    <div className="main-loader">
      <FadeLoader size={1000} />
    </div>
  ) : (
    <Modal
      show={show}
      onHide={() => {
        setIsEditing(false);
        handleClose();
      }}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        {/* <Modal.Title className="fw-bold text-dark">{title}</Modal.Title> */}
      </Modal.Header>

      <Modal.Body>
        {data ? (
          <section className="section">
            <div className="section-header">
              <h1>Weekly Reward Details</h1>
              <Button
                variant="secondary"
                size="sm"
                className="ms-3 themeclr"
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? "Cancel" : "Edit"}
              </Button>
            </div>

            <div className="section-body">
              <div className="card">
                <Row>
                  <Col lg="12" className="my-2 px-4">
                    <ul className="list-unstyled ps-0 mb-0 mt-3 row">
                      {renderField("Week No", "weekNo")}
                      {renderField("Year", "year")}
                      <li className="col-lg-4 col-sm-6 my-2"></li>
                      {renderCurrencyNonEditField(
                        "Last Week Remaining",
                        "prevWeekLeftAmount",
                      )}
                      {renderCurrencyNonEditField(
                        "Last Week Earning",
                        "lastWeekEarning",
                      )}
                      <li className="col-lg-4 col-sm-6 my-2"></li>
                      {renderCurrencyField("Total Budget", "totalWeeklyBudget")}

                      {renderCurrencyField(
                        "Jackpot Budget",
                        "jackpotBudgetTotal",
                      )}
                      {renderCurrencyField(
                        "Visible Jackpot Budget",
                        "jackpotBudgetVisible",
                      )}
                      {renderCurrencyField(
                        "Reserved Jackpot Budget",
                        "jackpotBudgetReserve",
                      )}

                      {renderCurrencyField(
                        "Visible Grand Jackpot",
                        "grandJackpotVisible",
                      )}
                      {renderCurrencyField(
                        "Visible Major Jackpot",
                        "majorJackpotVisible",
                      )}
                      {renderCurrencyField(
                        "Visible Minor Jackpot",
                        "minorJackpotVisible",
                      )}

                      {renderCurrencyField(
                        "Reserved Grand Jackpot",
                        "grandJackpotReserve",
                      )}
                      {renderCurrencyField(
                        "Reserved Major Jackpot",
                        "majorJackpotReserve",
                      )}
                      {renderCurrencyField(
                        "Reserved Minor Jackpot",
                        "minorJackpotReserve",
                      )}

                      <li className="col-lg-4 col-sm-6 my-2">
                        <p>
                          Created At :{" "}
                          <span className="font-weight-bold">
                            {data?.date_created_utc
                              ? DATE_FORMAT(data.date_created_utc)
                              : "--"}
                          </span>
                        </p>
                      </li>

                      <li className="col-lg-4 col-sm-6 my-2">
                        <p>
                          Modified At :{" "}
                          <span className="font-weight-bold">
                            {data?.date_modified_utc
                              ? DATE_FORMAT(data.date_modified_utc)
                              : "--"}
                          </span>
                        </p>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </div>
            </div>
          </section>
        ) : (
          <p>No data found</p>
        )}
      </Modal.Body>

      {isEditing && (
        <Modal.Footer>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
